import * as THREE from 'three';
import type { ICubePresentationBuilder } from './interfaces';

export class CubePresentation {
  private readonly _faces: THREE.Object3D;

  private readonly _edges: THREE.Object3D;

  private readonly _innerModel: THREE.Object3D;

  private readonly _viewCube: THREE.Object3D;

  private readonly _boundingSphere: THREE.Sphere;

  private readonly _faceMaterial: THREE.MeshBasicMaterial;

  private readonly _edgeMaterial: THREE.LineBasicMaterial;

  private readonly _defaultEdgeColor = new THREE.Color(0x00b1ea);

  private readonly _defaultFaceOpacity = 0.6;

  private readonly _highlightedFaceMaterial: THREE.MeshBasicMaterial;

  public constructor(builder: ICubePresentationBuilder, onReady: () => void) {
    this._faceMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0x9ee7ff),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: this._defaultFaceOpacity
    });

    this._highlightedFaceMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(0x1bc0ff),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.9
    });

    this._edgeMaterial = new THREE.LineBasicMaterial({
      color: this._defaultEdgeColor
    });

    this._boundingSphere = builder.getBoundingSphere();
    this._faces = builder.buildFaces(this._faceMaterial);
    this._edges = builder.buildEdges(this._edgeMaterial);
    this._innerModel = builder.buildInnerModel(onReady);
    this._viewCube = new THREE.Object3D().add(this._faces).add(this._edges).add(this._innerModel);
  }

  public getViewModel(): THREE.Object3D {
    return this._viewCube;
  }

  public getFaces(): THREE.Object3D {
    return this._faces;
  }

  public getBoundingSphere(): THREE.Sphere {
    return this._boundingSphere;
  }

  public applyDefaultFaceColor(): void {
    this._edgeMaterial.color = this._defaultEdgeColor;
    this._faceMaterial.opacity = this._defaultFaceOpacity;
    this._faces.children.forEach((face) => {
      const mesh = face as THREE.Mesh;
      mesh.material = this._faceMaterial;
    });
  }

  public applyTransparency(): void {
    this._edgeMaterial.color = new THREE.Color(0xffffff);
    this._faceMaterial.opacity = 0.25;
  }

  public highlightFace(face: THREE.Object3D): void {
    const mesh = face as THREE.Mesh;
    mesh.material = this._highlightedFaceMaterial;
  }
}
